import { Link } from 'react-router-dom'
import { VendorProduct } from '../types'

interface VendorProductGridProps {
  vendorId: string
  products: VendorProduct[]
  showAddButton?: boolean
  onAddProduct?: () => void
}

export default function VendorProductGrid({ vendorId, products, showAddButton, onAddProduct }: VendorProductGridProps) {
  
  if (products.length === 0) {
    return (
      <div className="product-grid-empty">
        <div className="empty-state">
          <h3>No Products Listed</h3>
          <p>This vendor hasn't added any products yet.</p>
          {showAddButton && (
            <button onClick={onAddProduct} className="add-product-btn">
              Add First Product
            </button>
          )}
        </div>
        
        <style>{`
          .product-grid-empty {
            text-align: center;
            padding: 60px 20px;
            background: #f9f9f9;
            border-radius: 8px;
            border: 2px dashed #ddd;
          }
          
          .empty-state h3 {
            color: #a0aec0;
            margin-bottom: 10px;
          }
          
          .empty-state p {
            color: #a0aec0;
            margin-bottom: 20px;
          }
          
          .add-product-btn {
            background: #2c5530;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
          }
          
          .add-product-btn:hover {
            background: #1e3a21;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="vendor-product-grid">
      <div className="products-header">
        <h3>Products ({products.length})</h3>
        {showAddButton && (
          <button onClick={onAddProduct} className="add-product-btn">
            Add Product
          </button>
        )}
      </div>
      
      <div className="products-grid">
        {products.map(product => (
          <div key={product.id} className="product-card">
            <Link to={`/vendor/${vendorId}/product/${product.id}`} className="product-link">
              
              {/* Product Image */}
              <div className="product-image">
                {product.images.length > 0 ? (
                  <img src={product.images[0]} alt={product.name} />
                ) : (
                  <div className="no-image">No Image</div>
                )}
                {product.featured && (
                  <div className="featured-badge">Featured</div>
                )}
                {!product.inStock && (
                  <div className="out-of-stock-overlay">Out of Stock</div>
                )}
              </div>

              {/* Product Info */}
              <div className="product-info">
                <h4 className="product-name">{product.name}</h4>
                <p className="product-type">{product.extractType} • {product.type}</p>
                
                {product.potency && (
                  <p className="product-potency">{product.potency}</p>
                )}
                
                <div className="product-rating">
                  {product.reviewCount > 0 ? (
                    <>
                      <span className="rating-stars">{'★'.repeat(Math.floor(product.rating))}</span>
                      <span className="rating-score">{product.rating}</span>
                      <span className="review-count">({product.reviewCount})</span>
                    </>
                  ) : (
                    <span className="no-reviews">No reviews yet</span>
                  )}
                </div>

                <div className="product-pricing">
                  {product.sizes.length > 0 && (
                    <>
                      <span className="price-from">From</span>
                      <span className="price">${Math.min(...product.sizes.map(s => s.price))}</span>
                      {product.sizes.length > 1 && (
                        <span className="size-options">• {product.sizes.length} sizes</span>
                      )}
                    </>
                  )}
                </div>

                <div className="product-meta">
                  <div className="product-tags">
                    {product.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="product-tag">{tag}</span>
                    ))}
                    {product.tags.length > 2 && (
                      <span className="more-tags">+{product.tags.length - 2}</span>
                    )}
                  </div>
                  
                  {product.salesCount > 0 && (
                    <div className="sales-count">{product.salesCount} sold</div>
                  )}
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <style>{`
        .vendor-product-grid {
          margin-top: 30px;
        }

        .products-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .products-header h3 {
          color: #2c5530;
          margin: 0;
        }

        .add-product-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }

        .add-product-btn:hover {
          background: #1e3a21;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .product-card {
          background: #2d3748;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        }

        .product-link {
          display: block;
          text-decoration: none;
          color: inherit;
        }

        .product-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image {
          width: 100%;
          height: 100%;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a0aec0;
          font-size: 14px;
        }

        .featured-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          background: linear-gradient(135deg, #ff6b6b, #ffa726);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .out-of-stock-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .product-info {
          padding: 16px;
        }

        .product-name {
          font-size: 16px;
          font-weight: 600;
          color: #2c5530;
          margin: 0 0 8px 0;
          line-height: 1.3;
        }

        .product-type {
          font-size: 13px;
          color: #a0aec0;
          margin: 0 0 6px 0;
        }

        .product-potency {
          font-size: 12px;
          color: #2c5530;
          background: #e8f5e8;
          padding: 2px 6px;
          border-radius: 4px;
          display: inline-block;
          margin: 0 0 10px 0;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
          font-size: 13px;
        }

        .rating-stars {
          color: #ffa726;
          font-size: 14px;
        }

        .rating-score {
          font-weight: 600;
          color: #e2e8f0;
        }

        .review-count {
          color: #a0aec0;
        }

        .no-reviews {
          color: #a0aec0;
          font-size: 12px;
        }

        .product-pricing {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
        }

        .price-from {
          font-size: 12px;
          color: #a0aec0;
        }

        .price {
          font-size: 18px;
          font-weight: bold;
          color: #2c5530;
        }

        .size-options {
          font-size: 12px;
          color: #a0aec0;
        }

        .product-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .product-tags {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .product-tag {
          background: #f0f0f0;
          color: #a0aec0;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
        }

        .more-tags {
          background: #e0e0e0;
          color: #a0aec0;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
        }

        .sales-count {
          font-size: 11px;
          color: #a0aec0;
        }

        @media (max-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 15px;
          }
          
          .product-image {
            height: 160px;
          }
          
          .product-info {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  )
}